import { IFixitAPIClient } from '@ifixit/ifixit-api-client';
import { captureMessage } from '@ifixit/sentry';
import * as z from 'zod';

export type User = z.infer<typeof AuthenticatedUserSchema>;

export const userKeys = {
   user: ['user'],
};

export const userDataLocalKey = 'user.data.5';

export const AuthenticatedUserSchema = z.object({
   id: z.number(),
   username: z.string(),
   handle: z.string().nullable(),
   login: z.string(),
   thumbnail: z.string().nullable(),
   algoliaApiKeyProducts: z.string().nullable(),
   priceTiers: z.record(z.string(), z.string()),
   isAdmin: z.boolean(),
   isAuthor: z.boolean().default(false),
   teams: z.array(z.number()),
   links: z.record(z.string(), z.any()),
   unread_notification_count: z.number().optional().nullable(),
   privileges: z.array(z.string()),
   greatest_privilege: z.string().optional(),
   isEmployee: z.boolean(),
   isModerator: z.boolean().default(false),
   isExemptFromAB: z.boolean(),
   partner: z.enum(['Back Market', 'THD']).nullable(),
   langid: z.string().nullable().default(null),
   subscriptionContracts: z.array(
      z.object({
         productHandle: z.string(),
         storeCode: z.string(),
         sku: z.string().nullable().optional(),
      })
   ),
});

const UserApiResponseSchema = z.object({
   userid: z.number(),
   username: z.string(),
   unique_username: z.string().optional().nullable(),
   login: z.string(),
   image: z
      .object({
         thumbnail: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
   algoliaApiKeyProducts: z.string().optional().nullable(),
   priceTiers: z.record(z.string(), z.string()),
   privileges: z.array(z.string()),
   greatest_privilege: z.string().optional(),
   teams: z.array(z.number()),
   links: z.record(z.string(), z.any()),
   is_employee: z.boolean().optional(),
   unread_notification_count: z.number().optional().nullable(),
   isExemptFromAB: z.boolean(),
   partner: z.enum(['Back Market', 'THD']).nullable().optional(),
   langid: z.string().optional().nullable(),
   subscriptionContracts: z
      .array(
         z.object({
            productHandle: z.string(),
            storeCode: z.string(),
            sku: z.string().nullable().optional(),
         })
      )
      .optional()
      .default([]),
});

export async function fetchAuthenticatedUser(
   apiClient: IFixitAPIClient,
   init?: RequestInit
): Promise<User | null> {
   const response = await apiClient.getRaw('user', 'user', init);
   if (response.status === 401 || response.status === 403) {
      return null;
   } else if (!response.ok) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
      apiClient.throwFetchError(response, {
         input: 'user',
         init,
      });
   }
   const payload = await response.json();
   if (payload == null) {
      return null;
   }
   const userSchema = UserApiResponseSchema.safeParse(payload);
   if (!userSchema.success) {
      captureMessage('User schema parsing failed', {
         level: 'debug',
         extra: {
            schema: userSchema,
            payload: payload,
            error: userSchema.error,
         },
      });
      return null;
   }
   const isAdmin = userSchema.data.privileges.includes('Admin');
   const isAuthor = ['Author', 'Author & Mod'].includes(userSchema.data.greatest_privilege ?? '');
   const isEmployee = userSchema.data?.is_employee ?? false;
   const isModerator = ['Moderator', 'Author & Mod'].includes(
      userSchema.data.greatest_privilege ?? ''
   );
   return {
      id: userSchema.data.userid,
      username: userSchema.data.username,
      handle: userSchema.data.unique_username ?? null,
      login: userSchema.data.login,
      thumbnail: userSchema.data.image?.thumbnail ?? null,
      algoliaApiKeyProducts: userSchema.data.algoliaApiKeyProducts ?? null,
      priceTiers: userSchema.data.priceTiers,
      isAdmin: isAdmin,
      isAuthor: isAuthor,
      teams: userSchema.data.teams,
      links: userSchema.data.links,
      unread_notification_count: userSchema.data.unread_notification_count,
      privileges: userSchema.data.privileges,
      greatest_privilege: userSchema.data.greatest_privilege,
      isEmployee: isEmployee,
      isModerator: isModerator,
      isExemptFromAB: userSchema.data?.isExemptFromAB ?? false,
      partner: userSchema.data.partner ?? null,
      langid: userSchema.data.langid ?? null,
      subscriptionContracts: userSchema.data.subscriptionContracts ?? [],
   };
}
